import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { MapPin, TrendingDown, TrendingUp, UserX, Users } from "lucide-react";
import { cn } from "@/lib/utils";

/* ---------- MD-only: Team Adoption at a glance ---------- */

const AdoptionStat = ({ label, value, sub, tone }) => (
  <div className="min-w-[120px]">
    <p className="text-[0.62rem] uppercase tracking-[0.12em] text-muted-foreground font-medium">{label}</p>
    <p className={cn("font-serif text-xl leading-tight", tone)}>{value}</p>
    {sub && <p className="text-[0.68rem] text-muted-foreground truncate">{sub}</p>}
  </div>
);

export const TeamAdoptionPanel = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/adoption/md").then((r) => setData(r.data))
      .catch((e) => console.error("Adoption snapshot fetch failed:", e));
  }, []);

  if (!data) return null;
  const trendUp = data.trend >= 0;
  return (
    <Card className="col-span-2 lg:col-span-12 order-4 lg:order-none" data-testid="team-adoption-panel">
      <CardContent className="py-3.5 flex flex-wrap items-center gap-x-8 gap-y-3">
        <span className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-medium text-[hsl(var(--cx-navy))]">
          <Users size={15} className="text-[hsl(var(--cx-gold))]" /> Team Adoption
        </span>
        <AdoptionStat label={`Login % · ${data.week_label}`}
          value={`${data.week_pct}%`}
          sub={`${data.logged_in_week} of ${data.total_active} users`} />
        <AdoptionStat label="Trend vs last week"
          value={
            <span className={cn("flex items-center gap-1", trendUp ? "text-[hsl(var(--status-green))]" : "text-[hsl(var(--cx-crimson))]")}>
              {trendUp ? <TrendingUp size={16} /> : <TrendingDown size={16} />}{trendUp ? "+" : ""}{data.trend} pts
            </span>
          } />
        {data.best_location && (
          <AdoptionStat label="Best location" value={`${data.best_location.pct}%`} sub={data.best_location.location}
            tone="text-[hsl(var(--status-green))]" />
        )}
        {data.worst_location && (
          <AdoptionStat label="Lowest location" value={`${data.worst_location.pct}%`} sub={data.worst_location.location}
            tone="text-[hsl(var(--cx-crimson))]" />
        )}
        <button
          className="ml-auto flex items-center gap-1.5 text-sm rounded-md border px-3 py-1.5 hover:bg-[hsl(var(--cx-surface))] transition-colors"
          onClick={() => setOpen(true)} data-testid="adoption-inactive-button">
          <UserX size={15} className="text-[hsl(var(--cx-crimson))]" />
          <span className="tabular font-semibold">{data.inactive_count}</span>
          <span className="text-xs text-muted-foreground">inactive {data.inactive_threshold}+ days</span>
        </button>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="adoption-inactive-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif font-normal">
              Inactive {data.inactive_threshold}+ days — {data.inactive_count} user{data.inactive_count !== 1 ? "s" : ""}
            </DialogTitle>
          </DialogHeader>
          {data.inactive_groups.length === 0 ? (
            <p className="text-sm text-muted-foreground">Everyone has logged in recently.</p>
          ) : (
            <div className="space-y-4">
              {data.inactive_groups.map((g) => (
                <div key={g.location}>
                  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-[hsl(var(--cx-navy))] mb-1.5">
                    <MapPin size={12} /> {g.location}
                  </p>
                  <div className="space-y-1">
                    {g.users.map((u) => (
                      <div key={u.user_id} className="flex items-center justify-between gap-2 text-sm px-2 py-1 rounded bg-[hsl(var(--cx-surface))]">
                        <span>{u.name} <span className="text-xs text-muted-foreground">· {u.role}</span></span>
                        <span className="text-[0.65rem] text-muted-foreground tabular whitespace-nowrap">{u.last_seen_fmt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
